import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Plus, X, FunnelSimple } from '@phosphor-icons/react';
import ListSearch, { filterBySearch } from './ListSearch';
import { formatUtcToIst, formatUtcToIstDate } from '../modules/tickets/utils/datetime';
import { ModuleShell } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
const API = apiPath('/api');

function ServiceCatalogPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    service_type: 'account_access',
    fields: {}
  });

  const serviceTypes = [
    { value: 'account_access', label: 'Account Access Request', fields: ['system_name', 'access_level', 'justification'] },
    { value: 'software_install', label: 'Software Installation', fields: ['software_name', 'version', 'business_need'] },
    { value: 'hardware_request', label: 'Hardware Request', fields: ['device_type', 'specifications', 'purpose'] },
    { value: 'network_access', label: 'Network Access', fields: ['network_name', 'duration', 'reason'] }
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filterStatus, searchQuery]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/service-requests`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...requests];

    if (filterStatus !== 'all') {
      result = result.filter(r => r.status === filterStatus);
    }
    result = filterBySearch(result, searchQuery, ['request_id', 'service_type', 'requester_name', 'status']);

    setFilteredRequests(result);
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/service-requests`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setFormData({ service_type: 'account_access', fields: {} });
      fetchRequests();
    } catch (error) {
      console.error('Failed to create request:', error);
      alert('Failed to create request');
    }
  };

  const currentServiceType = serviceTypes.find(st => st.value === formData.service_type);

  return (
    <ModuleShell
      title="Service Catalog"
      subtitle="Request IT services"
      headerActions={(
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="create-request-button"
        >
          <Plus size={18} weight="bold" />
          New Request
        </button>
      )}
    >
    <div data-testid="service-catalog-page">

      {/* Filters */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', padding: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <FunnelSimple size={20} weight="bold" />
          <span style={{ fontSize: '12px', fontWeight: '600' }}>Filters</span>
        </div>
        <div style={{ maxWidth: '300px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field"
            style={{ padding: '6px 8px', fontSize: '12px' }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search service requests…"
        resultCount={filteredRequests.length}
        totalCount={requests.length}
      />

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div>
          <div style={{ marginBottom: '6px', fontSize: '12px', color: '#6B7280' }}>
            Showing {filteredRequests.length} of {requests.length} requests
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Service Type</th>
                  <th>Requester</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => (
                  <tr key={request.request_id} data-testid="request-row">
                    <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: '600' }}>
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowViewModal(true);
                        }}
                        style={{ background: 'none', border: 'none', color: '#002FA7', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 'inherit' }}
                        data-testid="request-id-link"
                      >
                        {request.request_id}
                      </button>
                    </td>
                    <td>{request.service_type.replace('_', ' ')}</td>
                    <td>{request.requester_name}</td>
                    <td>
                      <span className={`badge badge-${request.status === 'pending' ? 'medium' : request.status === 'approved' ? 'low' : 'closed'}`}>{request.status}</span>
                    </td>
                    <td>{formatUtcToIstDate(request.created_at)}</td>
                    <td>
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowViewModal(true);
                        }}
                        className="btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                        data-testid="view-request-button"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        data-testid="create-request-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>New Service Request</h2>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateRequest}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Service Type</label>
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({ service_type: e.target.value, fields: {} })}
                  className="input-field"
                  data-testid="service-type-select"
                >
                  {serviceTypes.map((st) => (
                    <option key={st.value} value={st.value}>{st.label}</option>
                  ))}
                </select>
              </div>

              {currentServiceType?.fields.map((field) => (
                <div key={field} style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                    {field.replace('_', ' ')}
                  </label>
                  <input
                    type="text"
                    value={formData.fields[field] || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      fields: { ...formData.fields, [field]: e.target.value }
                    })}
                    className="input-field"
                    required
                    data-testid={`field-${field}`}
                  />
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="submit-request-button">
                  Submit Request
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="cancel-request-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        data-testid="view-request-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>{selectedRequest.request_id}</h2>
              <button onClick={() => setShowViewModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Service Type</p>
              <p style={{ fontSize: '16px', fontWeight: '600', textTransform: 'capitalize' }}>{selectedRequest.service_type.replace('_', ' ')}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Requester</p>
                <p style={{ fontSize: '12px', fontWeight: '500' }}>{selectedRequest.requester_name}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Status</p>
                <span className={`badge badge-${selectedRequest.status === 'pending' ? 'medium' : 'low'}`}>{selectedRequest.status}</span>
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>Request Details</p>
              {Object.entries(selectedRequest.fields).map(([key, value]) => (
                <div key={key} style={{ marginBottom: '8px', padding: '8px', background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                  <p style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px', textTransform: 'capitalize' }}>{key.replace('_', ' ')}</p>
                  <p style={{ fontSize: '12px', fontWeight: '500' }}>{value}</p>
                </div>
              ))}
            </div>

            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Created At</p>
              <p style={{ fontSize: '12px' }}>{formatUtcToIst(selectedRequest.created_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

export default ServiceCatalogPage;